import { supabase } from '@/integrations/supabase/client';
import Handlebars from 'handlebars';

interface UnaContext {
  unaId: string;
  unaName: string;
}

/**
 * Resolve the UNA context for a user - tries treasury secretary first, then member
 */
export async function resolveUnaForUser(userId: string): Promise<UnaContext | null> {
  // Try treasury secretary first
  const { data: treasuryUna } = await supabase
    .from('unas')
    .select('id, name')
    .eq('treasury_secretary_id', userId)
    .maybeSingle();

  if (treasuryUna) {
    return { unaId: treasuryUna.id, unaName: treasuryUna.name };
  }

  // Try as a member
  const { data: membership } = await supabase
    .from('una_members')
    .select('una_id, unas(id, name)')
    .eq('user_id', userId)
    .eq('status', 'active')
    .maybeSingle();

  if (membership?.unas) {
    const una = membership.unas as unknown as { id: string; name: string };
    return { unaId: una.id, unaName: una.name };
  }

  return null;
}

/**
 * Resolve UNA name by UNA ID
 */
export async function getUnaNameById(unaId: string): Promise<string | null> {
  const { data } = await supabase
    .from('unas')
    .select('name')
    .eq('id', unaId)
    .maybeSingle();

  return data?.name || null;
}

/**
 * Render HTML template with merge fields populated
 */
export function renderMergeFields(
  templateHtml: string,
  mergeData: Record<string, string>
): string {
  if (!templateHtml) return '';

  try {
    // Handle both uppercase and lowercase variants
    const normalizedData: Record<string, string> = {};
    
    for (const [key, value] of Object.entries(mergeData)) {
      normalizedData[key] = value;
      normalizedData[key.toLowerCase()] = value;
      normalizedData[key.toUpperCase()] = value;
    }

    // Add common date fields
    const now = new Date();
    normalizedData['current_date'] = now.toLocaleDateString('en-US', { 
      year: 'numeric', month: 'long', day: 'numeric' 
    });
    normalizedData['CURRENT_DATE'] = normalizedData['current_date'];
    normalizedData['current_year'] = now.getFullYear().toString();
    normalizedData['CURRENT_YEAR'] = normalizedData['current_year'];

    const template = Handlebars.compile(templateHtml);
    return template(normalizedData);
  } catch (error) {
    console.error('Error rendering merge fields:', error);
    return templateHtml;
  }
}

/**
 * Helper to create standard merge data with UNA name
 */
export function createMergeData(unaName: string): Record<string, string> {
  return {
    una_name: unaName,
    UNA_NAME: unaName,
  };
}
